export const calculateDifferences = <
  T extends Record<string, number | string | undefined>,
>(
  currentData: T,
  previousData: T,
): {
  [K in keyof T]: {
    absoluteDifference: string;
    relativeDifference: string | 'N/A';
  };
} => {
  // Initialize the result object with the expected structure
  const result = {} as {
    [K in keyof T]: {
      absoluteDifference: string;
      relativeDifference: string | 'N/A';
    };
  };

  // Iterate through all keys in currentData
  (Object.keys(currentData) as Array<keyof T>).forEach((key) => {
    // Ensure the key exists in previousData to avoid undefined values
    if (Object.hasOwn(previousData, key)) {
      // Convert values to numbers if they are stored as strings (e.g., percentages)
      const currentValue =
        currentData[key] !== undefined
          ? typeof currentData[key] === 'string'
            ? parseFloat(currentData[key] as string)
            : (currentData[key] as number)
          : 0;
      const previousValue =
        previousData[key] !== undefined
          ? typeof previousData[key] === 'string'
            ? parseFloat(previousData[key] as string)
            : (previousData[key] as number)
          : 0;

      // Calculate absolute difference
      const absoluteDifference = currentValue - previousValue;

      // Calculate relative difference as a percentage (avoid division by zero)
      const relativeDifference =
        previousValue !== 0
          ? (absoluteDifference / previousValue) * 100
          : 'N/A';

      // Format the results with + or - prefixes for better readability
      result[key] = {
        absoluteDifference: `${absoluteDifference >= 0 ? '+' : ''}${absoluteDifference}`,
        relativeDifference:
          typeof relativeDifference === 'number'
            ? `${relativeDifference >= 0 ? '+' : ''}${relativeDifference.toFixed(2)}%`
            : 'N/A',
      };
    }
  });

  return result;
};
